import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MailCheck, Send, Copy, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { agentUpdateAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * UpdateInfoLinkDialog — sends the agent a secure, pre-filled link to review
 * and update their own personal / contact details (e.g. a missing SSN). Unlike
 * onboarding this never creates a new account; it only updates the existing
 * agent record.
 */
export const UpdateInfoLinkDialog = ({ open, onOpenChange, agent }) => {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => { if (open) setResult(null); }, [open]);

  const email = agent?.email || '';

  const handleSend = async () => {
    if (!agent?.npn) return;
    setBusy(true);
    try {
      const res = await agentUpdateAPI.createLink(agent.npn);
      setResult(res.data);
      toast.success(res.data?.email_sent
        ? 'Update link sent to the agent'
        : 'Link created — email delivery pending, share the link below');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create update link'));
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = () => {
    if (!result?.update_link) return;
    navigator.clipboard.writeText(result.update_link);
    toast.success('Update link copied');
  };

  const handleClose = () => { onOpenChange(false); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(true); }}>
      <DialogContent data-testid="update-info-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <MailCheck size={18} /> Update Agent Info
          </DialogTitle>
          <DialogDescription>
            Email this agent a secure, pre-filled link so they can review and complete
            their own details (name, phone, date of birth, address and SSN). This does
            not change their portal access.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="py-3 space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
              <div className="text-slate-600 mb-1">A secure update link will be emailed to:</div>
              <div className="font-semibold text-slate-900">{agent?.first_name} {agent?.last_name}</div>
              <div className="text-xs text-slate-600 mt-2">
                <span className="font-medium">Email:</span>{' '}
                {email || <span className="text-red-600">(missing)</span>}
              </div>
            </div>
            {!email && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                This agent has no email on file. Add an email to the profile before sending an update link.
              </div>
            )}
            <p className="text-[11px] text-slate-500">
              The link is single-use, expires in 7 days and their SSN is shown masked (last 4 only).
            </p>
          </div>
        ) : (
          <div className="py-2 space-y-3">
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Update link ready.</div>
                <div className="text-xs mt-0.5">
                  {result.email_sent
                    ? 'The email is on its way to the agent.'
                    : 'Email delivery pending — copy the link below and share it with the agent.'}
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm space-y-1.5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Secure link</div>
              <div className="flex items-center gap-2">
                <div
                  className="font-mono text-xs text-slate-800 bg-slate-100 px-2 py-1 rounded border border-slate-200 truncate flex-1"
                  data-testid="update-info-link-url"
                >
                  {result.update_link}
                </div>
                <Button size="sm" variant="outline" onClick={handleCopy} data-testid="copy-update-link-btn">
                  <Copy size={14} />
                </Button>
              </div>
              <div className="text-[11px] text-slate-500">Sent to: {result.email}</div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={handleClose} data-testid="update-info-cancel-btn">Cancel</Button>
              <Button onClick={handleSend} disabled={busy || !email} data-testid="send-update-link-btn">
                <Send size={14} className="mr-1.5" /> {busy ? 'Sending…' : 'Send Update Link'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} data-testid="update-info-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateInfoLinkDialog;
